"use client"

import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded"
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded"
import AirlineSeatReclineExtraRoundedIcon from "@mui/icons-material/AirlineSeatReclineExtraRounded"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

const summary = [
  {
    label: "月度签单额",
    value: "¥286万",
    note: "较上月 +14.8%",
    tone: "#2563eb",
    icon: <TrendingUpRoundedIcon sx={{ fontSize: 18 }} />,
  },
  {
    label: "待回款",
    value: "¥84万",
    note: "未来 14 日应收 5 笔",
    tone: "#0f766e",
    icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} />,
  },
  {
    label: "在途服务",
    value: "16 单",
    note: "欧洲线占比 43%",
    tone: "#7c3aed",
    icon: <AirlineSeatReclineExtraRoundedIcon sx={{ fontSize: 18 }} />,
  },
  {
    label: "风险订单",
    value: "3 单",
    note: "付款延迟 / 资源待确认",
    tone: "#d97706",
    icon: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />,
  },
]

const revenueTrend = [
  { week: "W1", signed: 42, received: 31 },
  { week: "W2", signed: 56, received: 39 },
  { week: "W3", signed: 64, received: 48 },
  { week: "W4", signed: 58, received: 54 },
  { week: "W5", signed: 66, received: 59 },
  { week: "W6", signed: 72, received: 61 },
]

const pipelineData = [
  { stage: "新询单", count: 38, fill: "#93c5fd" },
  { stage: "需求澄清", count: 26, fill: "#60a5fa" },
  { stage: "已报价", count: 18, fill: "#3b82f6" },
  { stage: "待确认", count: 11, fill: "#2563eb" },
  { stage: "已转订单", count: 7, fill: "#1d4ed8" },
]

const destinationMix = [
  { name: "欧洲", value: 38, color: "#2563eb" },
  { name: "日本", value: 24, color: "#0f766e" },
  { name: "中东", value: 18, color: "#7c3aed" },
  { name: "海岛", value: 12, color: "#f59e0b" },
  { name: "其他", value: 8, color: "#94a3b8" },
]

const watchList = [
  { name: "瑞士亲子滑雪之旅", issue: "二次报价待确认", owner: "定制二组", amount: "¥23.4万" },
  { name: "北海道雪季私享团", issue: "酒店资源待锁定", owner: "运营组", amount: "¥18.8万" },
  { name: "迪拜商务接待项目", issue: "首付款尚未到账", owner: "财务组", amount: "¥31.2万" },
  { name: "摩洛哥深度文化线", issue: "签证资料补充中", owner: "定制一组", amount: "¥12.6万" },
]

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        border: "1px solid rgba(15,23,42,0.08)",
        background: "rgba(255,255,255,0.96)",
        boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
      }}
    >
      {label && (
        <Typography sx={{ mb: 0.5, fontSize: 11, color: "text.secondary" }}>
          {label}
        </Typography>
      )}
      {payload.map((item) => (
        <Typography key={item.name} sx={{ fontSize: 12, color: "text.primary" }}>
          {item.name} · {item.value}
        </Typography>
      ))}
    </Box>
  )
}

export default function DashboardPage() {
  return (
    <Box>
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: "0 !important" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {summary.map((item, index) => (
              <Box
                key={item.label}
                sx={{
                  borderRight: {
                    xs: "none",
                    xl: index < summary.length - 1 ? "1px solid rgba(15,23,42,0.08)" : "none",
                  },
                  borderBottom: {
                    xs: index < summary.length - 1 ? "1px solid rgba(15,23,42,0.08)" : "none",
                    sm: index < 2 ? "1px solid rgba(15,23,42,0.08)" : "none",
                    xl: "none",
                  },
                }}
              >
                <Box sx={{ px: 3, py: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{item.label}</Typography>
                      <Typography sx={{ mt: 1.25, fontSize: 34, fontWeight: 700, letterSpacing: -1.2, color: "text.primary" }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        placeItems: "center",
                        width: 40,
                        height: 40,
                        borderRadius: 3.5,
                        color: item.tone,
                        background: `${item.tone}12`,
                      }}
                    >
                      {item.icon}
                    </Box>
                  </Stack>

                  <Typography sx={{ mt: 2.75, fontSize: 12, color: "text.secondary" }}>
                    {item.note}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            xl: "minmax(0, 1.25fr) minmax(320px, 0.75fr)",
          },
          gap: 2.5,
          mb: 2.5,
        }}
      >
        <Box>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                <Box>
                  <Typography variant="h6">签单与回款走势</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
                    近 6 周签单额与实际回款走势
                  </Typography>
                </Box>
                <Chip label="Mock Data" size="small" />
              </Stack>

              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueTrend} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="signedFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="receivedFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0f766e" stopOpacity={0.14} />
                        <stop offset="95%" stopColor="#0f766e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="rgba(15,23,42,0.06)" />
                    <XAxis dataKey="week" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="signed" stroke="#2563eb" strokeWidth={2.5} fill="url(#signedFill)" name="签单额" />
                    <Area type="monotone" dataKey="received" stroke="#0f766e" strokeWidth={2.5} fill="url(#receivedFill)" name="回款额" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6">目的地结构</Typography>
              <Typography sx={{ mt: 0.5, mb: 2, fontSize: 12, color: "text.secondary" }}>
                当月签单目的地分布
              </Typography>

              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={destinationMix} dataKey="value" nameKey="name" innerRadius={54} outerRadius={82} paddingAngle={2}>
                      {destinationMix.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Stack spacing={1}>
                {destinationMix.map((item) => (
                  <Stack key={item.name} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{item.name}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{item.value}%</Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            xl: "minmax(320px, 0.72fr) minmax(0, 1.28fr)",
          },
          gap: 2.5,
        }}
      >
        <Box>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6">询单转化漏斗</Typography>
              <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: 12, color: "text.secondary" }}>
                以完整业务阶段观察当前转化情况
              </Typography>

              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineData} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid horizontal={false} stroke="rgba(15,23,42,0.05)" />
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="stage"
                      tickLine={false}
                      axisLine={false}
                      width={86}
                      tick={{ fill: "#475569", fontSize: 12 }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={18}>
                      {pipelineData.map((item) => (
                        <Cell key={item.stage} fill={item.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6">重点关注</Typography>
              <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: 12, color: "text.secondary" }}>
                当前最值得管理层关注的订单与风险项
              </Typography>

              <Stack divider={<Divider flexItem />} spacing={0}>
                {watchList.map((item) => (
                  <Stack
                    key={item.name}
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ py: 2.1 }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                        {item.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.9 }}>
                        <Chip
                          label={item.issue}
                          size="small"
                          sx={{
                            color: "#92400e",
                            background: "rgba(245,158,11,0.12)",
                            border: "1px solid rgba(245,158,11,0.12)",
                          }}
                        />
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{item.owner}</Typography>
                      </Stack>
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>
                      {item.amount}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  )
}
